/**
 * JsonToMarkdownConverter - JSON構造からMarkdown形式への変換
 * TipTapエディターのJSON形式をMarkdownテキストに変換
 */

import type { JSONContent } from '@tiptap/react';
import { createLogger } from '../utils/logger';

const log = createLogger('JsonToMarkdownConverter');

export interface MarkdownExportOptions {
  referenceStyleLinks?: boolean;
}

interface ExportContext {
  options: MarkdownExportOptions;
  links: Map<string, { href: string; title?: string }>;
  linkKeyMap: Map<string, string>; // href+title -> linkId (for O(1) lookup)
}

export class JsonToMarkdownConverter {
  // This prevents the class from being "static-only" for linting purposes while preserving the public API.

  private constructor() {
    // Intentionally empty.
  }

  /**
   * TipTap JSON構造をMarkdownテキストに変換
   */
  static convertToMarkdown(json: JSONContent, options: MarkdownExportOptions = {}): string {
    if (!json || !json.content) {
      return '';
    }

    const context: ExportContext = {
      options,
      links: new Map<string, { href: string; title?: string }>(),
      linkKeyMap: new Map<string, string>(),
    };

    let markdown = JsonToMarkdownConverter.processNodes(json.content, context);

    // 参照形式リンクの定義を文末に追加
    // referenceStyleLinks オプションが有効、または元々参照形式だったリンクがある場合
    if (context.links.size > 0) {
      markdown = `${markdown.trimEnd()}\n\n`;
      const linkEntries = Array.from(context.links.entries());
      for (const [id, data] of linkEntries) {
        // タイトル内のダブルクォートをエスケープ
        const titlePart = data.title ? ` "${data.title.replace(/"/g, '\\"')}"` : '';
        markdown += `[${id}]: ${data.href}${titlePart}\n`;
      }
    }

    return markdown;
  }

  /**
   * ノード配列を処理してMarkdownテキストを生成
   */
  private static processNodes(nodes: JSONContent[], context: ExportContext, depth = 0): string {
    return nodes.map((node) => JsonToMarkdownConverter.processNode(node, context, depth)).join('');
  }

  /**
   * 単一ノードを処理してMarkdownテキストを生成
   */
  private static processNode(node: JSONContent, context: ExportContext, depth = 0): string {
    if (!node.type) {
      return '';
    }

    switch (node.type) {
      case 'paragraph':
        return JsonToMarkdownConverter.processParagraph(node, context, depth);

      case 'heading':
        return JsonToMarkdownConverter.processHeading(node, context);

      case 'bulletList':
        return JsonToMarkdownConverter.processBulletList(node, context, depth);

      case 'orderedList':
        return JsonToMarkdownConverter.processOrderedList(node, context, depth);

      case 'listItem':
        return JsonToMarkdownConverter.processListItem(node, context, depth);

      case 'blockquote':
        return JsonToMarkdownConverter.processBlockquote(node, context);

      case 'codeBlock':
        return JsonToMarkdownConverter.processCodeBlock(node, context);

      case 'table':
        return JsonToMarkdownConverter.processTable(node, context);

      case 'tableRow':
        return JsonToMarkdownConverter.processTableRow(node, context);

      case 'tableHeader':
      case 'tableCell':
        return JsonToMarkdownConverter.processTableCell(node, context);

      case 'horizontalRule':
        return '\n---\n\n';

      case 'hardBreak':
        return '\n';

      case 'image':
        return JsonToMarkdownConverter.processImage(node);

      case 'text':
        return JsonToMarkdownConverter.processText(node, context);

      default:
        log.warn(`Unsupported node type: ${node.type}`);
        return node.content
          ? JsonToMarkdownConverter.processNodes(node.content, context, depth)
          : '';
    }
  }

  /**
   * 段落の処理
   */
  private static processParagraph(
    node: JSONContent,
    context: ExportContext,
    depth: number,
  ): string {
    const content = node.content
      ? JsonToMarkdownConverter.processNodes(node.content, context, depth)
      : '';
    return `${content}\n\n`;
  }

  /**
   * 見出しの処理
   */
  private static processHeading(node: JSONContent, context: ExportContext): string {
    const level = node.attrs?.level || 1;
    const content = node.content ? JsonToMarkdownConverter.processNodes(node.content, context) : '';
    const hashes = '#'.repeat(Math.min(Math.max(level, 1), 6));
    // NOTE: アンカーリンク用にID属性を保持する場合の構文は標準的ではないため、
    // ここでは単純に見出しテキストのみを返し、エディタ側のパーサーでIDを再生成する方針とする。
    // もし属性として明示的にIDを埋め込みたい場合は {#id} などの構文を検討。
    return `${hashes} ${content}\n\n`;
  }

  /**
   * 箇条書きリストの処理
   */
  private static processBulletList(
    node: JSONContent,
    context: ExportContext,
    depth: number,
  ): string {
    const items = node.content || [];
    const content = items
      .map((item) => JsonToMarkdownConverter.processListItemWithMarker(item, context, depth, '-'))
      .join('');
    return `${content}\n`;
  }

  /**
   * 番号付きリストの処理
   */
  private static processOrderedList(
    node: JSONContent,
    context: ExportContext,
    depth: number,
  ): string {
    const items = node.content || [];
    const content = items
      .map((item, idx) =>
        JsonToMarkdownConverter.processListItemWithMarker(item, context, depth, `${idx + 1}.`),
      )
      .join('');
    return `${content}\n`;
  }

  /**
   * リストアイテムの処理
   */
  private static processListItem(node: JSONContent, context: ExportContext, depth: number): string {
    // Fallback: if listItem appears without a parent list, treat as a bullet item.
    return JsonToMarkdownConverter.processListItemWithMarker(node, context, depth, '-');
  }

  private static processListItemWithMarker(
    node: JSONContent,
    context: ExportContext,
    depth: number,
    marker: string,
  ): string {
    const indent = '  '.repeat(depth);
    const children = node.content || [];

    // TipTap listItem content is typically: paragraph + (optional nested list)
    let firstLine = '';
    const rest: JSONContent[] = [];

    for (const child of children) {
      if (child.type === 'paragraph' && firstLine === '') {
        firstLine = child.content
          ? JsonToMarkdownConverter.processNodes(child.content, context, 0).trim()
          : '';
        continue;
      }
      rest.push(child);
    }

    let out = `${indent}${marker} ${firstLine}\n`;

    for (const child of rest) {
      if (child.type === 'bulletList' || child.type === 'orderedList') {
        // Nested lists should be indented one level deeper
        out += JsonToMarkdownConverter.processNode(child, context, depth + 1);
        continue;
      }

      if (child.type === 'paragraph') {
        const text = child.content
          ? JsonToMarkdownConverter.processNodes(child.content, context, 0).trim()
          : '';
        if (text) {
          out += `${indent}  ${text}\n`;
        }
        continue;
      }

      out += JsonToMarkdownConverter.processNode(child, context, depth + 1);
    }

    return out;
  }

  /**
   * 引用の処理
   */
  private static processBlockquote(node: JSONContent, context: ExportContext): string {
    const content = node.content ? JsonToMarkdownConverter.processNodes(node.content, context) : '';
    return `${content
      .split('\n')
      .filter((line) => line.trim())
      .map((line) => `> ${line}`)
      .join('\n')}\n\n`;
  }

  /**
   * コードブロックの処理
   */
  private static processCodeBlock(node: JSONContent, context: ExportContext): string {
    const language = node.attrs?.language || '';
    const content = node.content ? JsonToMarkdownConverter.processNodes(node.content, context) : '';
    return `\`\`\`${language}\n${content.trim()}\n\`\`\`\n\n`;
  }

  /**
   * 画像の処理
   */
  private static processImage(node: JSONContent): string {
    const src = node.attrs?.src || '';
    const alt = node.attrs?.alt || '';
    return `![${alt}](${src})`;
  }

  /**
   * テーブルの処理
   */
  private static processTable(node: JSONContent, context: ExportContext): string {
    if (!node.content || node.content.length === 0) {
      return '';
    }

    const rows = node.content.map((row) => JsonToMarkdownConverter.processTableRow(row, context));
    let markdown = rows.join('');

    // ヘッダー行の区切り線を追加（2行目に）
    const lines = markdown.trim().split('\n');
    if (lines.length >= 2 && lines[0]) {
      // NOTE: `processTableRow` always emits a leading and trailing `|`.
      // `split('|')` therefore includes empty tokens at both ends.
      // We derive column count from token length to preserve empty cells (e.g. "|||").
      const headerParts = lines[0].split('|');
      const columnCount = Math.max(1, headerParts.length - 2);
      const headerSeparator = `|${Array.from({ length: columnCount })
        .map(() => '---')
        .join('|')}|`;
      lines.splice(1, 0, headerSeparator);
      markdown = `${lines.join('\n')}\n\n`;
    }

    return markdown;
  }

  /**
   * テーブル行の処理
   */
  private static processTableRow(node: JSONContent, context: ExportContext): string {
    if (!node.content) {
      return '';
    }

    const cells = node.content.map((cell) => {
      const content = JsonToMarkdownConverter.processTableCell(cell, context);
      const normalized = content.trim().replace(/\n/g, ' '); // セル内改行を除去
      // Empty cells must still include a placeholder (space) to be recognized as a table by parsers.
      return normalized.length === 0 ? ' ' : normalized;
    });

    return `|${cells.join('|')}|\n`;
  }

  /**
   * テーブルセルの処理
   */
  private static processTableCell(node: JSONContent, context: ExportContext): string {
    return node.content ? JsonToMarkdownConverter.processNodes(node.content, context) : '';
  }

  /**
   * テキストの処理（マークアップ適用）
   */
  private static processText(node: JSONContent, context: ExportContext): string {
    let text = node.text || '';

    if (node.marks) {
      for (const mark of node.marks) {
        switch (mark.type) {
          case 'bold':
            text = `**${text}**`;
            break;
          case 'italic':
            text = `*${text}*`;
            break;
          case 'code':
            text = `\`${text}\``;
            break;
          case 'strike':
            text = `~~${text}~~`;
            break;
          case 'link': {
            const href = mark.attrs?.href || '#';
            const title = mark.attrs?.title || '';
            const isReference = mark.attrs?.isReference;
            const referenceId = mark.attrs?.referenceId;

            // 元々参照形式だった場合、または referenceStyleLinks オプションが有効な場合
            if (isReference && referenceId && typeof referenceId === 'string') {
              // 元の参照IDを使用
              const key = `${href}|||${title}`;
              let linkId = context.linkKeyMap.get(key);

              if (!linkId) {
                // 元の参照IDを保持
                linkId = referenceId;
                context.links.set(linkId, { href, title });
                context.linkKeyMap.set(key, linkId);
              }

              text = `[${text}][${linkId}]`;
            } else if (context.options.referenceStyleLinks) {
              // O(1)でリンクIDを検索（パフォーマンス改善）
              const key = `${href}|||${title}`;
              let linkId = context.linkKeyMap.get(key);

              if (!linkId) {
                linkId = (context.links.size + 1).toString();
                context.links.set(linkId, { href, title });
                context.linkKeyMap.set(key, linkId);
              }

              text = `[${text}][${linkId}]`;
            } else {
              const titlePart = title ? ` "${title}"` : '';
              text = `[${text}](${href}${titlePart})`;
            }
            break;
          }
        }
      }
    }

    return text;
  }

  /**
   * Markdownファイルとしてダウンロード
   */
  static downloadAsMarkdown(
    json: JSONContent,
    filenameOrOptions?: string | MarkdownExportOptions,
    options?: MarkdownExportOptions,
  ): void {
    // 引数の解析（後方互換性のため）
    let filename = 'document.md';
    let exportOptions: MarkdownExportOptions = {};

    if (typeof filenameOrOptions === 'string') {
      filename = filenameOrOptions;
      exportOptions = options || {};
    } else if (filenameOrOptions) {
      exportOptions = filenameOrOptions;
    }

    try {
      const markdownContent = JsonToMarkdownConverter.convertToMarkdown(json, exportOptions);

      if (!markdownContent.trim()) {
        log.warn('Empty content, skipping download');
        return;
      }

      const blob = new Blob([markdownContent], { type: 'text/markdown;charset=utf-8' });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      const downloadFilename = filename.endsWith('.md') ? filename : `${filename}.md`;
      link.download = downloadFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);

      log.debug(`Downloaded: ${downloadFilename}`);
    } catch (error) {
      log.error('Download failed:', error);
      throw new Error('Markdownファイルのダウンロードに失敗しました');
    }
  }
}

export default JsonToMarkdownConverter;
